import { Navigate, useSearchParams } from 'react-router-dom';
import { useProfile } from '@/lib/providers';
import { useAdminPreview } from '@/lib/admin-preview-context';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { SamenChat } from '@/components/samen/SamenChat';
import { TeamMeditationCalendar } from '@/components/team/TeamMeditationCalendar';
import { MeditationCountdown } from '@/components/team/MeditationCountdown';
import { PlanSamenMeditationDialog } from '@/components/samen/PlanSamenMeditationDialog';
import { SectionBackground } from '@/components/ui/section-background';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Camera, Pencil, Check, X } from 'lucide-react';
import { useEffect, useRef, useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import Loading from '@/components/loading';
import { Samen } from '@/types/custom';
import { useSamenMeditations } from '@/lib/hooks/use-samen-meditations';
import { SamenMeditationSession } from '@/components/samen/SamenMeditationSession';
import imageCompression from 'browser-image-compression';
import { AvatarCropDialog } from '@/components/profile/avatar-crop-dialog';
import { CreateSamenGroupCard } from '@/components/samen/CreateSamenGroupCard';
import { InviteSamenMemberDialog } from '@/components/samen/InviteSamenMemberDialog';
import { PendingSamenInvites } from '@/components/samen/PendingSamenInvites';
import { LeaveSamenGroupSection } from '@/components/samen/LeaveSamenGroupSection';

interface SamenMember {
  id: string;
  naam: string | null;
  avatar_url: string | null;
}

export default function SamenPage() {
  const { profile, loadingProfile, refreshProfile } = useProfile();
  const { effectiveRole } = useAdminPreview();
  const [members, setMembers] = useState<SamenMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'leden');
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  // Bumped after an invite is sent so the pending list picks it up.
  const [inviteRefreshKey, setInviteRefreshKey] = useState(0);

  // Samen name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Samen avatar upload
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [samenAvatarUrl, setSamenAvatarUrl] = useState<string | null>(null);

  const canAccess = effectiveRole === 'hoofdhartuser' || profile?.role === 'hoofdhartuser';
  const samen: Samen | null | undefined = profile?.samen;

  useEffect(() => {
    if (samen?.avatar_url) setSamenAvatarUrl(samen.avatar_url);
  }, [samen?.avatar_url]);

  const startEditingName = () => {
    setNameValue(samen?.name || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const saveSamenName = async () => {
    if (!samen || nameValue.trim() === samen.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const { error } = await supabase
        .from('samen')
        .update({ name: nameValue.trim() })
        .eq('id', samen.id);
      if (error) throw error;
      refreshProfile();
    } catch (err) {
      console.error('Failed to update samen name:', err);
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    setShowCropDialog(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!samen) return;
    setShowCropDialog(false);
    setUploadingAvatar(true);
    try {
      const compressed = await imageCompression(
        new File([blob], 'avatar.webp', { type: 'image/webp' }),
        { maxSizeMB: 0.2, maxWidthOrHeight: 400, useWebWorker: true },
      );
      const filePath = `samen/${samen.id}/avatar.webp`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressed, { upsert: true, contentType: 'image/webp' });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('samen')
        .update({ avatar_url: avatarUrl })
        .eq('id', samen.id);
      if (updateError) throw updateError;

      setSamenAvatarUrl(avatarUrl);
      refreshProfile();
    } catch (err) {
      console.error('Samen avatar upload failed:', err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const {
    meditations,
    hasUpcoming,
    nextMeditation,
    activeMeditation,
    activeMeditations,
    deleteMeditation,
    refetch: refetchMeditations,
  } = useSamenMeditations(samen?.id);

  // Pick a session to render when none is open, but never swap mid-session.
  useEffect(() => {
    if (currentSessionId) {
      if (!activeMeditations.some((m) => m.id === currentSessionId)) {
        setCurrentSessionId(null);
      }
      return;
    }
    if (!sessionDismissed && activeMeditations.length > 0) {
      setCurrentSessionId(activeMeditations[0].id);
    }
  }, [activeMeditations, currentSessionId, sessionDismissed]);

  const currentSession = currentSessionId
    ? activeMeditations.find((m) => m.id === currentSessionId) ?? null
    : null;

  useEffect(() => {
    if (!samen) return;

    const fetchMembers = async () => {
      setLoadingMembers(true);

      // Step 1: get user_ids in this samen group
      const { data: links, error: linksError } = await supabase
        .from('samen_user_link')
        .select('user_id')
        .eq('samen_id', samen.id);

      if (linksError) {
        console.error('Error fetching samen links:', linksError);
        setLoadingMembers(false);
        return;
      }

      if (links && links.length > 0) {
        const userIds = links.map((l) => l.user_id);

        // Step 2: fetch profiles for those user_ids
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, naam, avatar_url')
          .in('id', userIds);

        if (profilesError) {
          console.error('Error fetching member profiles:', profilesError);
        }

        if (profiles) {
          setMembers(profiles as SamenMember[]);
        }
      }

      setLoadingMembers(false);
    };

    fetchMembers();
  }, [samen?.id]);

  // Realtime presence — reuses the same channel as SamenChat
  useEffect(() => {
    if (!samen || !profile) return;

    const channel = supabase.channel(`samen-presence-${samen.id}`, {
      config: { presence: { key: profile.id } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const ids = new Set(
          Object.values(state)
            .flat()
            .map((p: any) => p.id as string)
        );
        setOnlineUserIds(ids);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            id: profile.id,
            naam: profile.naam ?? 'Onbekend',
            avatar_url: profile.avatar_url,
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [samen?.id, profile?.id]);

  // Sort members: online first, then alphabetical
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aOnline = onlineUserIds.has(a.id);
      const bOnline = onlineUserIds.has(b.id);
      if (aOnline !== bOnline) return aOnline ? -1 : 1;
      return (a.naam ?? '').localeCompare(b.naam ?? '');
    });
  }, [members, onlineUserIds]);

  if (loadingProfile) return <Loading />;
  if (!canAccess) return <Navigate to="/" />;

  if (!samen) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center">
        <CreateSamenGroupCard onCreated={refreshProfile} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative group">
            <Avatar className="w-20 h-20">
              {samenAvatarUrl && <AvatarImage src={samenAvatarUrl} />}
              <AvatarFallback>
                <Users className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
            >
              <Camera className="h-6 w-6 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            {uploadingAvatar && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <AvatarCropDialog
            file={cropFile}
            open={showCropDialog}
            onClose={() => setShowCropDialog(false)}
            onCrop={handleCroppedUpload}
          />
          <div className="text-center">
            {editingName ? (
              <div className="flex items-center justify-center gap-2">
                <input
                  ref={nameInputRef}
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveSamenName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  className="text-2xl font-bold text-center bg-transparent border-b-2 border-primary outline-none w-48"
                  disabled={savingName}
                />
                <button type="button" onClick={saveSamenName} disabled={savingName} className="text-green-600 hover:text-green-700">
                  <Check className="h-5 w-5" />
                </button>
                <button type="button" onClick={() => setEditingName(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <h1
                className="text-2xl font-bold flex items-center justify-center gap-2 group/name cursor-pointer"
                onClick={startEditingName}
              >
                {samen.name}
                <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover/name:opacity-100 transition-opacity" />
              </h1>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              {members.length} {members.length === 1 ? 'persoon' : 'personen'}
            </p>
          </div>
        </div>

        {/* Active meditation banner */}
        {activeMeditation && !currentSession && (
          <MeditationCountdown
            meditation={null}
            activeMeditation={activeMeditation as any}
            variant="samen"
            onSwitchToTab={() => {
              setSessionDismissed(false);
              setCurrentSessionId(activeMeditation.id);
              setActiveTab('samen-meditatie');
            }}
          />
        )}

        {/* Tabs */}
        <div ref={tabsRef}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-2 h-auto">
              <TabsTrigger value="leden" className="text-xs sm:text-sm py-2">
                Mijn Samen Groep
              </TabsTrigger>
              <TabsTrigger value="samen-meditatie" className="text-xs sm:text-sm py-2 relative">
                Plan Samen een Activiteit
                {hasUpcoming && (
                  <span className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full ${activeMeditation ? 'bg-green-500 animate-pulse' : 'bg-green-500'}`} />
                )}
              </TabsTrigger>
            </TabsList>

            {/* Leden tab */}
            <TabsContent value="leden" className="mt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Mijn Samen Groep
                  </h2>
                  <InviteSamenMemberDialog
                    samenId={samen.id}
                    onInvited={() => setInviteRefreshKey((k) => k + 1)}
                  />
                </div>
                {loadingMembers ? (
                  <Loading />
                ) : (
                  <div className="flex flex-wrap gap-6">
                    {sortedMembers.map((member) => {
                      const isOnline = onlineUserIds.has(member.id);
                      const isMe = member.id === profile?.id;
                      const initials = (member.naam ?? '?')
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2);
                      return (
                        <div key={member.id} className={`flex flex-col items-center gap-2 group/member ${!isOnline ? 'grayscale opacity-50' : ''}`} title={isOnline ? 'Online' : 'Offline'}>
                          <div className={`rounded-full ${isOnline ? 'ring-[3px] ring-green-500 ring-offset-2' : ''}`}>
                            <Avatar className="h-20 w-20">
                              {member.avatar_url && <AvatarImage src={member.avatar_url} />}
                              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                            </Avatar>
                          </div>
                          <span className="text-sm font-medium text-center">
                            {member.naam || 'Onbekend'}{isMe ? ' (jij)' : ''}
                          </span>
                          {isOnline ? (
                            <span className="text-xs text-green-600 font-medium">Online</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Offline</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <PendingSamenInvites samenId={samen.id} refreshKey={inviteRefreshKey} />
              <SamenChat samenId={samen.id} onlineCount={onlineUserIds.size} />
            </TabsContent>

            {/* Samen Meditatie tab */}
            <TabsContent value="samen-meditatie" className="mt-4">
              <div className="space-y-6">
                {/* Active meditation session (fullscreen) */}
                {currentSession && (
                  <SamenMeditationSession
                    key={currentSession.id}
                    meditation={currentSession}
                    samenId={samen.id}
                    onEnded={() => {
                      setSessionDismissed(true);
                      setCurrentSessionId(null);
                      refetchMeditations();
                    }}
                  />
                )}

                <SectionBackground className="w-full mx-auto bg-white p-4">
                  <h3 className="text-lg font-medium">Plan Samen een Activiteit</h3>
                  <p className="text-muted-foreground mt-2">
                    Door op de knop hieronder te drukken kan je een Activiteit plannen met je groep. Dit kunnen alle soorten meditaties, ademhalingsoefeningen, lichaamsbewustzijnsoefeningen of Dance Breaks zijn.
                  </p>
                  <div className="mt-4">
                    <PlanSamenMeditationDialog
                      samenId={samen.id}
                      onCreated={refetchMeditations}
                    />
                  </div>
                </SectionBackground>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                  <TeamMeditationCalendar
                    meditations={meditations as any}
                    activeMeditationId={activeMeditation?.id}
                    activeMeditationIds={activeMeditations.map((m) => m.id)}
                    onDelete={deleteMeditation}
                    onOpenActive={(id) => {
                      setSessionDismissed(false);
                      setCurrentSessionId(id);
                    }}
                    title="Samen Activiteiten Kalender"
                    variant="samen"
                  />
                  <SamenChat samenId={samen.id} meditationId={activeMeditation?.id ?? nextMeditation?.id} onlineCount={onlineUserIds.size} />
                </div>

                {/* Countdown to next meditation */}
                {!activeMeditation && (
                  <MeditationCountdown
                    meditation={nextMeditation as any}
                    activeMeditation={null}
                    variant="samen"
                  />
                )}
              </div>
            </TabsContent>

          </Tabs>
        </div>

        <LeaveSamenGroupSection
          groupName={samen.name}
          memberCount={members.length}
          onLeft={refreshProfile}
        />
      </div>
    </div>
  );
}
